import styled from 'styled-components'
import React, { useRef, useState, type JSX } from 'react'
import { space, SpaceProps } from 'styled-system'
import { useDidMountEffect } from '@zuto/utilities'
import {
  RadioBox, RadioButtonProps, StyledRadio, StyledRadioGroup
} from '@zuto/atoms'
import { RadioButtonVariant } from '@zuto/atoms/src/RadioButton'
import Accordion, { AccordionProps } from '../Accordion'

export interface ExtendableRadioOption {
  value: string
  label: string
  /** Used to move the option to the accordion */
  hidden?: boolean
}

export interface ExtendableRadiosProps extends Omit<RadioButtonProps, 'direction' | 'variant' | 'alignment'> {
  options: ExtendableRadioOption[]
  title: string
  toggleChanged?: (state: boolean) => void
  variant?: RadioButtonVariant
  prominentColourScheme?: boolean
  removeContinueCta?: boolean
}

const StyledAccordion = styled(Accordion) <AccordionProps & SpaceProps>`
  ${space}
`

const ExtendableRadioWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
`

const ExtendableRadios = ({
  defaultState = '',
  options,
  id,
  title,
  prominentColourScheme = false,
  removeContinueCta = false,
  valueChanged,
  onClick,
  toggleChanged,
  checkedVariant,
  variant = 'standard',
  ...props
}: ExtendableRadiosProps) => {
  const [selected, setSelected] = useState<string | number>(defaultState)
  const accordionRef = useRef<React.ElementRef<typeof Accordion>>(null)
  const firstHiddenInputRef = useRef<HTMLLabelElement>(null)
  const defaultSelectedRadioRef = useRef<HTMLLabelElement>(null)
  const normalOptions = options.filter((option) => !option.hidden)
  const hiddenOptions = options.filter((option) => option.hidden)
  // the accordion starts open when the option selected on arrival is one of the hidden ones
  const [showHiddenOptionByDefault] = useState(() => hiddenOptions.some((option) => option.value === defaultState))

  useDidMountEffect(() => {
    if (valueChanged && selected && !removeContinueCta) {
      const parsedValue = typeof selected === 'number' ? `${selected}` : selected
      valueChanged(parsedValue)
    }
  }, [selected])

  const handleOptionClick = (value: string | number) => {
    if (valueChanged) {
      // Added timeout delay here so that radio buttons show a quick preview of focused/checked state before moving to next question
      setTimeout(() => {
        const parsedValue = typeof value === 'number' ? `${value}` : value
        valueChanged(parsedValue)
      }, 150)
    }
  }

  const noWrapper = (value: string | number, children: JSX.Element) => (
    <React.Fragment key={value.toString()}>{children}</React.Fragment>
  )

  const optionWrapper = (
    hidden: boolean | undefined,
    value: string | number,
    children: JSX.Element
  ) => noWrapper(value, children)

  const onFocus = (hidden?: boolean) => {
    if (accordionRef.current && hidden) {
      accordionRef.current.toggleAccordion()
    }
  }

  const handleAccordionToggle = (open: boolean) => {
    if (!defaultSelectedRadioRef.current && firstHiddenInputRef && firstHiddenInputRef.current) {
      firstHiddenInputRef.current.focus()
    }
    return toggleChanged ? toggleChanged(open) : () => { }
  }

  const setRadioRef = (value: string | number) => {
    if (!showHiddenOptionByDefault && value === 'agency') {
      return firstHiddenInputRef
    }
    if (defaultState === value) {
      return defaultSelectedRadioRef
    }
    return null
  }

  const determineDoubleColumnRadioVariantClassName = variant === 'experimentalDoubleColumnSmall' ? 'experimentalDoubleColumnSmall' : 'standard'
  const determineProminentColourSchemeRadioVariantClassName = prominentColourScheme ? 'experimentalDoubleColumnSmallProminentColourScheme' : determineDoubleColumnRadioVariantClassName

  const generateOptions = ({ value, label, hidden }: ExtendableRadioOption) => optionWrapper(
    hidden,
    value,
    <>
      <StyledRadio
        name={id}
        id={`${id}+${value.toString()}`}
        value={value}
        checked={selected === value}
        aria-checked={selected === value}
        onChange={() => setSelected(value)}
        onFocus={() => onFocus(hidden)}
        data-testid="extendable-radio-input"
      />
      <RadioBox
        ref={setRadioRef(value)}
        className={determineProminentColourSchemeRadioVariantClassName}
        htmlFor={`${id}+${value.toString()}`}
        $variant={variant}
        $alignment="left"
        data-testid="extendable-radio-option"
        onClick={removeContinueCta ? () => handleOptionClick(value) : onClick}
      >
        {label}
      </RadioBox>
    </>
  )

  const renderNormalOptions = () => {
    const normalGeneratedOptions = normalOptions.map(generateOptions)

    return <ExtendableRadioWrapper>{normalGeneratedOptions}</ExtendableRadioWrapper>
  }

  const renderHiddenOptions = () => {
    const hiddenGeneratedOptions = hiddenOptions.map(generateOptions)

    return hiddenGeneratedOptions
  }

  return (
    <StyledRadioGroup
      {...props}
      role="radiogroup"
      id={id}
      aria-label="Selection"
      $checkedVariant={checkedVariant}
      $direction="vertical"
      $variant={variant}
    >
      {renderNormalOptions()}
      <StyledAccordion
        defaultState={showHiddenOptionByDefault}
        toggleChanged={handleAccordionToggle}
        mt={3}
        title={title}
        ref={accordionRef}
      >
        {renderHiddenOptions()}
      </StyledAccordion>
    </StyledRadioGroup>
  )
}

export default ExtendableRadios
